// Action bar for wind detail: Edit / Close Cycle (when open) / Stop Session.
// Mirrors legacy updateWindDetailActions + closeWindFromDetail + stopWindFromDetail.
// Back button dropped — browser back covers it and legacy did the same.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { WindDetailData } from "@/hooks/use-wind-detail";
import { apiPost, apiDelete } from "@/lib/api-client";
import EditWindModal from "@/components/wind/edit-wind-modal";

interface WindActionsProps {
  data: WindDetailData;
}

// Shared action-button shape — identical to GridActions so all detail views
// render their header buttons at the same height regardless of variant.
const BTN_BASE =
  "inline-flex items-center justify-center h-7 px-3 text-xs font-medium rounded border transition-colors disabled:opacity-50";
const BTN_GHOST = `${BTN_BASE} border-border text-dim hover:text-text hover:border-dim`;
const BTN_WARN = `${BTN_BASE} border-warn/40 text-warn hover:bg-warn/10`;
const BTN_DANGER = `${BTN_BASE} border-danger/40 text-danger hover:bg-danger/10`;

export default function WindActions({ data }: WindActionsProps) {
  const { session, latest_cycle } = data;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["wind-detail", session.id] });
    qc.invalidateQueries({ queryKey: ["wind"] });
  };

  const closeMut = useMutation({
    mutationFn: () => apiPost(`/wind/${session.id}/close`),
    onSuccess: invalidate,
    onError: (e: Error) => alert(e.message || "Failed to close cycle"),
  });

  const stopMut = useMutation({
    mutationFn: () => apiDelete(`/wind/${session.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wind"] });
      navigate("/wind");
    },
    onError: (e: Error) => alert(e.message || "Failed to stop session"),
  });

  const cycleStatus = latest_cycle?.status ?? null;
  const hasOpenCycle = cycleStatus === "open" || cycleStatus === "closing";
  const isStopped = session.status === "stopped";
  const isBusy = closeMut.isPending || stopMut.isPending;

  return (
    <>
      {!isStopped && (
        <button className={BTN_GHOST} onClick={() => setShowEdit(true)}>
          Edit
        </button>
      )}

      {hasOpenCycle && (
        <button
          className={BTN_WARN}
          disabled={isBusy}
          onClick={() => {
            if (confirm("Close this wind cycle now?")) closeMut.mutate();
          }}
        >
          {closeMut.isPending ? "Closing…" : "Close Cycle"}
        </button>
      )}

      {!isStopped && (
        <button
          className={BTN_DANGER}
          disabled={isBusy}
          onClick={() => {
            if (
              confirm(
                "Stop this wind session? (no re-open, current cycle will close)"
              )
            )
              stopMut.mutate();
          }}
        >
          {stopMut.isPending ? "Stopping…" : "Stop Session"}
        </button>
      )}

      {showEdit && (
        <EditWindModal data={data} onClose={() => setShowEdit(false)} />
      )}
    </>
  );
}
